import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Search } from "lucide-react";
import Papa from "papaparse";
import Sidebar from "../../../components/Sidebar";
import { HEADING_COLOR } from "../../../constants/colors";

const CareerSelection = () => {
  const navigate = useNavigate();
  const [careers, setCareers] = useState([]);
  const [filteredCareers, setFilteredCareers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Fetch careers from CSV file (same as profile page)
    const csvFilePath = "/output.csv";
    
    fetch(csvFilePath)
      .then((response) => response.text())
      .then((csvText) => {
        Papa.parse(csvText, {
          header: true,
          skipEmptyLines: true,
          complete: (result) => {
            const careerOptions = result.data.map((row) => ({
              id: row.id,
              name: row.name,
            }));
            setCareers(careerOptions);
            setFilteredCareers(careerOptions);
            setIsLoading(false);
          },
        });
      })
      .catch((error) => {
        console.error("Error fetching CSV:", error);
        setIsLoading(false);
      });
  }, []);

  useEffect(() => {
    // Filter careers based on search term
    if (searchTerm.trim() === "") {
      setFilteredCareers(careers);
    } else {
      const filtered = careers.filter((career) =>
        career.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredCareers(filtered);
    }
  }, [searchTerm, careers]);

  const handleCareerSelect = (career) => {
    // Navigate back to AI Job Impact Assessment with the selected career
    navigate("/dashboard/career-assessment/ai-literacy-assessment", { 
      state: { selectedCareer: career.name } 
    });
  };

  const handleBackClick = () => {
    navigate(-1);
  };

  if (isLoading) {
    return (
      <Sidebar>
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading careers...</p>
          </div>
        </div>
      </Sidebar>
    );
  }

  return (
    <Sidebar>
      <div className="min-h-screen">
        <div className="max-w-6xl mx-auto px-6 py-8">
          {/* Header Section */}
          <div className="flex items-center gap-4 mb-8">
            <button
              onClick={handleBackClick}
              className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-6 h-6 text-gray-600" />
            </button>
            <div>
              <h1 className="text-3xl font-bold mb-1" style={{ color: HEADING_COLOR }}>
                Select Your Target Career
              </h1>
              <p className="text-medium text-gray-500">
                Choose an occupation to assess its AI impact
              </p>
            </div>
          </div>

          {/* Search Bar */}
          <div className="mb-8">
            <div className="relative max-w-2xl">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                placeholder="Search occupations..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Career Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredCareers.map((career) => (
              <button
                key={career.id}
                onClick={() => handleCareerSelect(career)}
                className="bg-white border border-gray-300 rounded-xl p-3 text-left hover:border-purple-400 hover:shadow-md transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              >
                <span className="text-gray-800 font-medium">{career.name}</span>
              </button>
            ))}
          </div>

          {/* No results message */}
          {filteredCareers.length === 0 && searchTerm.trim() !== "" && (
            <div className="text-center py-12">
              <p className="text-gray-500 text-lg">
                No careers found matching "{searchTerm}"
              </p>
              <button
                onClick={() => setSearchTerm("")}
                className="mt-4 text-purple-600 hover:text-purple-700 underline"
              >
                Clear search
              </button>
            </div>
          )}

          {/* Results count */}
          {searchTerm.trim() !== "" && (
            <div className="mt-6 text-center text-gray-500">
              Showing {filteredCareers.length} of {careers.length} careers
            </div>
          )}
        </div>
      </div>
    </Sidebar>
  );
};

export default CareerSelection; 